import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  MapPin,
  Phone,
  Mail,
  Star,
  Clock,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp, stagger, imageReveal } from "@/lib/motion";
import { services } from "@/lib/services";

const SITE_URL = "https://psyloveyourself.com";
const PAGE_URL = `${SITE_URL}/psixologos-patra`;

const neighborhoods = [
  "Κέντρο Πάτρας",
  "Ψηλά Αλώνια",
  "Άγιος Ανδρέας",
  "Παραλία Πατρών",
  "Ρίο",
  "Πανεπιστημιούπολη",
  "Άγιος Βασίλειος",
  "Συνοικισμός",
  "Βότσι",
  "Κανακάρη",
  "Παντοκράτορας",
  "Βραχνέικα",
];

const reasons = [
  {
    title: "Εξειδίκευση σε CBT",
    desc: "Γνωσιακή Συμπεριφορική Ψυχοθεραπεία — η πιο ερευνητικά τεκμηριωμένη μέθοδος παγκοσμίως.",
  },
  {
    title: "Δια ζώσης στο κέντρο της Πάτρας",
    desc: "Στη Ρήγα Φεραίου 119, σε εύκολα προσβάσιμη τοποθεσία με parking στην περιοχή.",
  },
  {
    title: "Online για όλη την Ελλάδα",
    desc: "Αν δεν μπορείς να έρθεις από κοντά, δεν χάνεις ποιότητα. Ασφαλής βιντεοκλήση.",
  },
  {
    title: "Ειδικές τιμές",
    desc: "Ειδική τιμολόγηση για άνεργους, φοιτητές και ΑμεΑ. Η θεραπεία πρέπει να είναι προσιτή.",
  },
];

export default function PsixologosPatra() {
  const schema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "MedicalBusiness"],
    name: "Άννα Παπαχρονόπουλου · Ψυχολόγος στην Πάτρα",
    url: PAGE_URL,
    image:
      "https://customer-assets.emergentagent.com/job_therapy-anna-gr/artifacts/nx9midrs_IMG_7568.jpeg",
    telephone: "+30-697-8042360",
    email: "anniepapach@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Ρήγα Φεραίου 119",
      addressLocality: "Πάτρα",
      addressRegion: "Αχαΐα",
      postalCode: "26221",
      addressCountry: "GR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 38.246639,
      longitude: 21.734573,
    },
    areaServed: neighborhoods.map((n) => ({
      "@type": "Place",
      name: `${n}, Πάτρα`,
    })),
    medicalSpecialty: "Psychiatric",
    priceRange: "€€",
    inLanguage: "el-GR",
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Αρχική", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Ψυχολόγος Πάτρα",
        item: PAGE_URL,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>
          {"Ψυχολόγος Πάτρα · Άννα Παπαχρονόπουλου | Ψυχοθεραπεία CBT"}
        </title>
        <meta
          name="description"
          content="Αναζητάς ψυχολόγο στην Πάτρα; Άννα Παπαχρονόπουλου — από τους ψυχολόγους της Πάτρας με εξειδίκευση στη Γνωσιακή Συμπεριφορική Ψυχοθεραπεία (CBT): άγχος, OCD, αυτοεκτίμηση, τραύμα. Ρήγα Φεραίου 119 ή online. Ειδικές τιμές για ανέργους & φοιτητές."
        />
        <meta
          name="keywords"
          content="ψυχολόγος Πάτρα, ψυχολόγοι Πάτρα, ψυχοθεραπεία Πάτρα, ψυχολόγος κέντρο Πάτρας, CBT Πάτρα, καλός ψυχολόγος Πάτρα, ψυχολόγος Ρήγα Φεραίου, ψυχολόγος Αχαΐα, online ψυχολόγος Πάτρα"
        />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Ψυχολόγος Πάτρα · Άννα Παπαχρονόπουλου"
        />
        <meta
          property="og:description"
          content="Ατομική ψυχοθεραπεία CBT στην Πάτρα — άγχος, OCD, αυτοεκτίμηση, τραύμα. Δια ζώσης ή online."
        />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbs)}
        </script>
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24" data-testid="patra-page">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20">
          {/* Hero */}
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>
            <p className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/60 text-[11px] tracking-[0.22em] uppercase font-body font-medium text-foreground/70 mb-6">
              <MapPin size={14} strokeWidth={1.5} className="text-primary" />
              Πάτρα · Ρήγα Φεραίου 119
            </p>
            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[72px] leading-[1.04]">
              Ψυχολόγος <span className="italic font-light">στην Πάτρα.</span>
              <br />
              <span className="text-foreground/85">
                Με ανθρώπινη <span className="italic font-light">προσέγγιση.</span>
              </span>
            </h1>
            <p className="mt-7 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed max-w-[680px]">
              Είμαι η Άννα Παπαχρονόπουλου, ψυχολόγος εγκατεστημένη στο κέντρο
              της Πάτρας. Παρέχω ατομική ψυχοθεραπεία βασισμένη σε{" "}
              <strong className="text-foreground font-normal">
                Γνωσιακή Συμπεριφορική Θεραπεία (CBT)
              </strong>{" "}
              — μία από τις πιο τεκμηριωμένες προσεγγίσεις διεθνώς.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-base font-medium hover:-translate-y-0.5 transition-all duration-300"
                data-testid="patra-hero-cta"
              >
                Κλείσε Συνεδρία
                <ArrowRight size={18} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-transparent px-8 py-4 font-body text-base font-medium text-foreground hover:bg-card transition-all"
              >
                <Phone size={16} strokeWidth={1.5} />
                697 8042 360
              </a>
            </div>
          </motion.div>

          {/* Why */}
          <motion.div
            {...stagger(0.08)}
            className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6"
          >
            {reasons.map((r) => (
              <motion.div
                key={r.title}
                variants={{
                  initial: { opacity: 0, y: 24 },
                  whileInView: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                  },
                }}
                className="bg-card border border-border/50 rounded-3xl p-7 sm:p-8"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                  <Check size={18} strokeWidth={2} />
                </div>
                <h2 className="font-heading text-2xl text-foreground tracking-tight">
                  {r.title}
                </h2>
                <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">
                  {r.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Areas served */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-20 p-8 sm:p-12 bg-card rounded-3xl border border-border/50"
          >
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Περιοχές εξυπηρέτησης
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
              Σε ποιες περιοχές της Πάτρας έρχονται οι ασθενείς μου
            </h2>
            <p className="mt-4 font-body font-light text-foreground/70 max-w-[640px] leading-relaxed">
              Το γραφείο μου βρίσκεται στη <strong className="text-foreground font-normal">Ρήγα Φεραίου 119</strong>, σε
              κεντρική και εύκολα προσβάσιμη τοποθεσία της Πάτρας. Δέχομαι
              ασθενείς από όλες τις γειτονιές και δήμους της Αχαΐας, καθώς και
              online συνεδρίες σε όλη την Ελλάδα.
            </p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {neighborhoods.map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1.5 text-sm font-body text-foreground/80 bg-background border border-border/60 rounded-full px-4 py-2"
                  data-testid={`area-${n}`}
                >
                  <MapPin size={11} strokeWidth={1.5} className="text-primary" />
                  {n}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Services list */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-20"
          >
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-4">
              Υπηρεσίες
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
              Με τι μπορώ να σε βοηθήσω στην Πάτρα
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((s) => (
                <Link
                  key={s.slug}
                  to={`/ipiresies/${s.slug}`}
                  className="group flex items-start justify-between gap-4 bg-background border border-border/60 rounded-2xl p-6 hover:bg-card transition-all duration-500"
                >
                  <span className="font-heading text-xl text-foreground tracking-tight">
                    {s.h1}
                  </span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.5}
                    className="text-foreground/60 mt-1 transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
              ))}
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-20 p-10 sm:p-14 bg-foreground text-background rounded-3xl"
          >
            <div className="flex items-center gap-2 mb-5">
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
              <Star size={14} fill="currentColor" />
            </div>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight leading-[1.05]">
              Έτοιμη να ξεκινήσεις
              <br />
              <span className="italic font-light">στην Πάτρα ή online;</span>
            </h2>
            <p className="mt-5 font-body font-light text-background/85 leading-relaxed max-w-[520px]">
              Η πρώτη συνεδρία είναι ένας ασφαλής χώρος γνωριμίας — χωρίς
              δέσμευση. Μπορείς να επικοινωνήσεις τηλεφωνικά, με email ή μέσω
              της φόρμας στην αρχική.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background text-foreground px-7 py-3.5 font-body text-sm font-medium hover:-translate-y-0.5 transition-all"
                data-testid="patra-final-cta"
              >
                Κλείσε Συνεδρία
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+306978042360"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background/30 px-7 py-3.5 font-body text-sm font-medium text-background hover:bg-background/10 transition-all"
              >
                <Phone size={14} strokeWidth={1.5} />
                697 8042 360
              </a>
              <a
                href="mailto:anniepapach@gmail.com"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background/30 px-7 py-3.5 font-body text-sm font-medium text-background hover:bg-background/10 transition-all"
              >
                <Mail size={14} strokeWidth={1.5} />
                Email
              </a>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
